<template>
  <section :class="['detail-section', {flush}, {edit:isEditing}]">
    <div class="d-flex flex-row align-items-center justify-content-between">
      <div class="section-title">{{ title }}</div>
      <icon-button
        v-if="editable"
        @click="doToggle"
        size="sm"
        :icon="isEditing ? 'x-lg' : 'pencil'"
        no-padding
        class="btn-edit-section"
        :disabled="isSaving"
      />
      <slot name="buttons" />
    </div>
    <slot :isEditing="isEditing" :isSaving="isSaving" />
    <div v-if="isEditing" class="d-flex flex-column gap-3 align-items-end mt-3">
      <b-textarea
        v-model="note"
        :min-rows="1"
        small
        :placeholder="notePlaceholder"
        :disabled="isSaving"
      />
      <div class="d-flex flex-row align-items-center gap-3">
        <button type="button" class="btn btn-sm btn-outline-dark" @click="doCancel" :disabled="isSaving">
          Cancel
        </button>
        <button type="button" class="btn btn-sm btn-dark" @click="doCommit" :disabled="isSaving">
          Save
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import IconButton from "@/components/common/IconButton.vue";
import BTextarea from "@/components/common/BTextarea.vue";

export default {
  components: {BTextarea, IconButton},
  emits: ['edit', 'cancel', 'saved'],
  props: {
    title: {
      type: String,
      required: true
    },
    flush: Boolean,
    editable: Boolean,
    commit: Function,
    notePlaceholder: {
      type: String,
      default: 'Describe this change (optional)'
    }
  },
  data() {
    return {
      isEditing: false,
      isSaving: false,
      error: null,
      note: ''
    };
  },
  methods: {
    async doCommit() {
      if (this.commit) {
        this.isSaving = true;
        this.error = null;
        try {
          await this.commit(this.note);
          this.note = '';
          this.isEditing = false;
          this.$emit('saved');
        } catch (e) {
          console.warn(`Could not update details.`, e);
          this.error = e;
        } finally {
          this.isSaving = false;
        }
      }
    },
    doToggle() {
      if (this.isEditing) {
        this.doCancel();
        return;
      }

      this.error = null;
      this.note = '';
      this.$emit('edit');
      this.isEditing = true;
    },
    doCancel() {
      this.error = null;
      this.note = '';
      this.isEditing = false;
      this.$emit('cancel');
    }
  }
}
</script>

<style scoped>
.detail-section {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-bottom: 1px solid var(--bs-border-color);
}
.detail-section.flush {
  padding: 1rem 0 0;
  gap: 0;
}
.detail-section .section-title {
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.75;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
  line-height: 26px;
}
.detail-section.flush .section-title {
  padding-left: 1rem;
  padding-right: 1rem;
}
.detail-section :deep(.section-content) {
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.detail-section.edit :deep(.section-content) {
  margin-bottom: 8px;
}
.detail-section :deep(.section-label) {
  line-height: 1rem;
  font-size: 0.875rem;
  color: var(--bs-secondary-color);
}
.detail-section.edit :deep(.section-label) {
  font-weight: 500;
  margin-bottom: 2px;
}
.detail-section :deep(.section-detail) {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex-grow: 1;
}
</style>
